import type { TWeather } from "@/types";
import ApiClient from "./fuelandmoreapi";
import { config } from "../config";

// Simple singleton for in-flight request de-duplication only.
// Keys are normalized to lowercase UN/LOCODEs to ensure identical requests coalesce.
// We rely on the browser's HTTP cache for subsequent requests after the first completes.

const api = new ApiClient(config.apiBaseUrl || "");
const inflight = new Map<string, Promise<TWeather[]>>();

const MAX_RETRIES = 2; // total attempts = MAX_RETRIES + 1
const BASE_DELAY_MS = 250;
const JITTER_MS = 100;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function getWeatherForPortCached(
  unlocode: string,
  _opts?: { ttlMs?: number; forceRefresh?: boolean }
): Promise<TWeather[]> {
  const key = (unlocode || "").toLowerCase();

  // If a request is already in-flight for this key, return the same promise
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = (async () => {
    let attempt = 0;
    while (true) {
      try {
        const data = await api.getWeatherForPort(key);
        return data as TWeather[];
      } catch (e) {
        if (attempt >= MAX_RETRIES) {
          return [] as TWeather[];
        }
        const backoff =
          BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * JITTER_MS;
        attempt += 1;
        await sleep(backoff);
      }
    }
  })().finally(() => {
    inflight.delete(key);
  });

  inflight.set(key, p);
  return p;
}

// No-op for compatibility; caching is delegated to the browser's HTTP cache.
export function clearWeatherCache(_unlocode?: string) {}
